// filepath: apps/web/src/features/promote/DiffView.tsx
//
// FR-UI-PROM-1 AC-1: renders each DefinitionDiff as a change card.
// INV-9 compliant: all server-supplied strings rendered as text nodes only,
// no dangerouslySetInnerHTML.

import React from "react";
import type { DefinitionDiff } from "./types";
import { IrreversibilityWarning } from "./IrreversibilityWarning";

interface DiffViewProps {
  diffs: DefinitionDiff[];
}

/**
 * DiffView renders the list of definition diffs as change cards.
 * Each card shows the definition id, type, and a summary of changes.
 */
export function DiffView({ diffs }: DiffViewProps): React.ReactElement {
  if (!diffs || diffs.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">No changes to review.</p>
    );
  }
  return (
    <ul className="space-y-4" data-testid="diff-view">
      {diffs.map((diff) => (
        <li key={`${diff.type}:${diff.id}`}>
          <DiffCard diff={diff} />
        </li>
      ))}
    </ul>
  );
}

function DiffCard({ diff }: { diff: DefinitionDiff }): React.ReactElement {
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-sm">{diff.id}</span>
        <span className="text-xs text-muted-foreground uppercase">
          {diff.type}
        </span>
      </div>

      {diff.irreversible &&
        diff.contract_steps &&
        diff.contract_steps.length > 0 && (
          <IrreversibilityWarning contractSteps={diff.contract_steps} />
        )}

      <ChangeSummary diff={diff} />
    </div>
  );
}

function ChangeSummary({ diff }: { diff: DefinitionDiff }): React.ReactElement {
  const lines: string[] = [];

  if (diff.added != null) {
    lines.push(`+ ${summaryLine(diff.added)}`);
  }
  if (diff.removed != null) {
    lines.push(`- ${summaryLine(diff.removed)}`);
  }
  if (diff.changed != null) {
    lines.push(`~ ${summaryLine(diff.changed)}`);
  }

  if (lines.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No structural changes.</p>
    );
  }

  return (
    <div className="text-sm text-muted-foreground font-mono whitespace-pre-wrap">
      {lines.join("\n")}
    </div>
  );
}

function summaryLine(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
